import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { hasAccess } from 'helpers/roleAccess';
import { RootState } from 'slices';
import { getRoleFromAccessToken } from 'helpers/jwt';
import { getRoleBasedUrl, extractRoleFromUrl } from './allRoutes';

const normalizeRole = (value?: string | null): string => {
  if (!value) return '';

  const normalized = value.trim().toLowerCase();

  if (
    normalized === 'sub admin' ||
    normalized === 'sub_admin' ||
    normalized === 'subadmin'
  ) {
    return 'sub_admin';
  }

  if (
    normalized === 'agent (internal)' ||
    normalized === 'agent_internal' ||
    normalized === 'agentinternal'
  ) {
    return 'agent (internal)';
  }

  return normalized;
};

export default function RoleProtected({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const { user } = useSelector((s: RootState) => s.Login);

  const role =
    ((user as any)?.role as string | undefined) ||
    getRoleFromAccessToken();

  const normalizedUserRole = normalizeRole(role);

  // Extract role from URL and check if it matches user role.
  const { role: urlRole, cleanPath } = extractRoleFromUrl(
    location.pathname
  );

  const normalizedUrlRole = normalizeRole(urlRole);

  // Compare normalized role names so aliases such as
  // "Sub Admin", "sub_admin", and "subadmin" are equivalent.
  if (
    normalizedUrlRole &&
    normalizedUrlRole !== normalizedUserRole
  ) {
    console.log(
      `URL role (${urlRole}) doesn't match user role (${role})`
    );

    const redirectPath = getRoleBasedUrl('/dashboard', role);
    return <Navigate to={redirectPath} replace />;
  }

  // If URL has no role prefix but the current user expects
  // role-prefixed routes, redirect to the prefixed equivalent.
  if (!urlRole && role) {
    if (
      normalizedUserRole === 'agent (internal)' ||
      normalizedUserRole === 'finance' ||
      normalizedUserRole === 'support' ||
      normalizedUserRole === 'sub_admin'
    ) {
      const prefixed = getRoleBasedUrl(cleanPath, role);

      if (prefixed !== location.pathname) {
        return <Navigate to={prefixed} replace />;
      }
    }
  }

  if (!hasAccess(location.pathname, role)) {
    const roleLower = normalizedUserRole;

    if (
      roleLower === 'roll caller' ||
      roleLower === 'roll_caller'
    ) {
      return <Navigate to="/players" replace />;
    }

    if (roleLower === 'finance') {
      return (
        <Navigate
          to={getRoleBasedUrl('/payments', role)}
          replace
        />
      );
    }

    if (roleLower === 'support') {
      return (
        <Navigate
          to={getRoleBasedUrl('/dashboard', role)}
          replace
        />
      );
    }

    if (roleLower === 'agent (internal)') {
      return (
        <Navigate
          to={getRoleBasedUrl('/dashboard', role)}
          replace
        />
      );
    }

    if (roleLower === 'sub_admin') {
      return (
        <Navigate
          to={getRoleBasedUrl('/dashboard', role)}
          replace
        />
      );
    }

    if (roleLower === 'superadmin') {
      return (
        <Navigate
          to={getRoleBasedUrl('/users', role)}
          replace
        />
      );
    }

    return (
      <Navigate
        to={getRoleBasedUrl('/dashboard', role)}
        replace
      />
    );
  }

  return <>{children}</>;
}
